import { styled } from "goober";
import { useCallback, useEffect, useState } from "react";
import type { ChatTool, ToolUseProps } from "../components/Chat/ChatTool";
import { ToolStatus } from "../components/Chat/ToolStatus";
import { ClickButton } from "../components/ClickButton";
import { Icon } from "../components/Icon";
import { Keycap } from "../components/Keycap";
import { KeyboardHint } from "../components/KeyboardHint";
import { Text } from "../components/Text";
import { mobileMedia } from "../hooks/useIsMobile";
import { maybeDisplayBrowserNotification } from "../lib/browserNotifications";

type Input = {
  title?: unknown;
  plan?: unknown;
};

type Output = {
  approved?: unknown;
};

export function parseApprovePlanInput(
  input: unknown
): { title: string; plan: string } | null {
  if (!input || typeof input !== "object" || Array.isArray(input)) return null;
  const { title, plan } = input as Input;
  if (typeof title !== "string" || typeof plan !== "string") return null;
  return { title, plan };
}

function parseOutput(output: unknown): { approved: boolean } | null {
  if (!output || typeof output !== "object" || Array.isArray(output))
    return null;
  const approved = (output as Output).approved;
  return typeof approved === "boolean" ? { approved } : null;
}

function ApprovePlanHeader({ toolUse, result, interrupted }: ToolUseProps) {
  const input = parseApprovePlanInput(toolUse.input);
  if (!result || result.isError) {
    return (
      <ToolStatus
        icon="list-todo"
        text={input ? `Plan ready: ${input.title}` : "Plan ready"}
        pending={!interrupted}
      />
    );
  }

  const output = parseOutput(result.output);
  if (!output?.approved) {
    return <ToolStatus icon="circle-minus" text="Plan needs more work" />;
  }

  return (
    <ToolStatus
      icon="check-filled"
      iconColor="var(--green-600)"
      text={input ? `Plan approved: ${input.title}` : "Plan approved"}
    />
  );
}

function ApprovePlanBody({ toolUse, result }: ToolUseProps) {
  const input = parseApprovePlanInput(toolUse.input);
  const output = result ? parseOutput(result.output) : null;
  if (!input) return null;

  return (
    <div style={{ padding: "2px 6px 8px 6px" }}>
      {output?.approved && (
        <Text variant="text-smaller-md" color="var(--text-tertiary)">
          Approved
        </Text>
      )}
    </div>
  );
}

const PromptContainer = styled("div")`
  border-radius: 12px;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  padding: 4px;
`;

const PromptBody = styled("div")`
  display: flex;
  flex-direction: column;
  padding-bottom: 4px;
  gap: 4px;
`;

const PromptHeader = styled("div")`
  display: flex;
  padding: 10px 8px;
  box-sizing: content-box;
  align-items: baseline;
  color: var(--text-secondary);
`;

const Row = styled(ClickButton)`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  padding-right: 28px;
  cursor: pointer;
  border-radius: 8px;
  position: relative;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
  &[data-highlighted="true"] {
    background: var(--alpha4);
  }
`;

const ActionIcon = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const Footer = styled("div")`
  display: flex;
  align-items: center;
  margin: 0 -4px -4px -4px;
  height: 40px;
  padding: 0 12px;
  border-top: 0.5px solid var(--border-alpha);
  justify-content: space-between;
  ${mobileMedia} {
    display: none;
  }
`;

const Instructions = styled("div")`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  gap: 4px;
`;

function ApprovePlanPrompt({
  onApprove,
  onContinue,
}: {
  onApprove: () => void;
  onContinue: () => void;
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    maybeDisplayBrowserNotification("3B needs you", "Approve the plan");
  }, []);

  const approve = useCallback(() => onApprove(), [onApprove]);
  const continuePlanning = useCallback(() => onContinue(), [onContinue]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        continuePlanning();
        return;
      }
      if (e.key === "1") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex(0);
        approve();
        return;
      }
      if (e.key === "2") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex(1);
        continuePlanning();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((index) => Math.min(index + 1, 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((index) => Math.max(index - 1, 0));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        if (selectedIndex === 0) approve();
        else continuePlanning();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [approve, continuePlanning, selectedIndex]);

  return (
    <PromptContainer>
      <PromptBody>
        <PromptHeader>
          <Text variant="text-small">What would you like to do?</Text>
        </PromptHeader>
        <Row
          data-no-pill
          onMouseEnter={() => setSelectedIndex(0)}
          onClick={approve}
          data-highlighted={selectedIndex === 0}
        >
          <ActionIcon>
            <Icon name="check" size={16} color="var(--text-primary)" />
          </ActionIcon>
          <Text variant="text-regular" color="var(--text-primary)">
            Approve and build
          </Text>
          {selectedIndex === 0 && (
            <KeyboardHint style={{ position: "absolute", right: 8, top: 8 }}>
              <Keycap>
                <Icon name="corner-down-left" size={12} />
              </Keycap>
            </KeyboardHint>
          )}
        </Row>
        <Row
          data-no-pill
          onMouseEnter={() => setSelectedIndex(1)}
          onClick={continuePlanning}
          data-highlighted={selectedIndex === 1}
        >
          <ActionIcon>
            <Icon name="pencil" size={16} color="var(--text-primary)" />
          </ActionIcon>
          <Text variant="text-regular" color="var(--text-primary)">
            Keep planning
          </Text>
          {selectedIndex === 1 && (
            <KeyboardHint style={{ position: "absolute", right: 8, top: 8 }}>
              <Keycap>
                <Icon name="corner-down-left" size={12} />
              </Keycap>
            </KeyboardHint>
          )}
        </Row>
      </PromptBody>
      <Footer>
        <Instructions>
          <Text variant="text-smaller-md">Keep planning</Text>
          <Keycap style={{ padding: "2px 4px" }}>
            <Text variant="text-smaller-md">esc</Text>
          </Keycap>
        </Instructions>
        <Instructions>
          <Text variant="text-smaller-md">Select</Text>
          <Keycap>
            <Icon name="corner-down-left" size={12} />
          </Keycap>
        </Instructions>
      </Footer>
    </PromptContainer>
  );
}

export const approvePlan: ChatTool = {
  name: "approvePlan",
  Header: ApprovePlanHeader,
  Body: ApprovePlanBody,
  alwaysLoadOutput: true,
  renderPendingPrompt: ({ input, onResult }) => {
    const parsed = parseApprovePlanInput(input);
    if (!parsed) return null;
    return (
      <ApprovePlanPrompt
        onApprove={() =>
          onResult({ approved: true }, false, {
            sendStagedAttachments: true,
            approvedPlan: true,
          })
        }
        onContinue={() =>
          onResult({ approved: false }, false, { pauseAfter: true })
        }
      />
    );
  },
};
